import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import ApiService from "./api-service";
import { firebaseConfig } from "../firebase";
import { getOrCreateDeviceId } from "../utils/device";

const VAPID_KEY = "BJCG_uCYDuMm66Q1iSMMESoWeR84UChInwsgHg06amqoPOx39CaP6jXM5JuHnYKVdP_US3oOZTXCPvVN-q8IgAk";
const TOKEN_KEY = "siki-web-fcm-token";
const TOKEN_USER_KEY = "siki-web-fcm-user";

let messagingRef;
let swRegistration;
let foregroundUnsubscribe;
let initializing;

const ensureMessaging = async () => {
  if (typeof window === "undefined") {
    return null;
  }
  const supported = await isSupported().catch(() => false);
  if (!supported) {
    return null;
  }
  if (!messagingRef) {
    const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
    messagingRef = getMessaging(app);
  }
  return messagingRef;
};

const ensureServiceWorker = async () => {
  if (swRegistration || typeof window === "undefined") {
    return swRegistration;
  }
  if (!("serviceWorker" in navigator)) {
    return null;
  }
  swRegistration = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
  if (!swRegistration) {
    swRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  }
  return swRegistration;
};

const shouldSyncToken = (token, userId) => {
  if (!token || !userId) {
    return false;
  }
  try {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(TOKEN_USER_KEY);
    return storedToken !== token || storedUser !== userId;
  } catch (e) {
    return true;
  }
};

const storeToken = (token, userId) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_USER_KEY, userId);
  } catch (e) {
  }
};

const requestBrowserPermission = async () => {
  if (typeof Notification === "undefined") {
    return false;
  }
  if (Notification.permission === "granted") {
    return true;
  }
  if (Notification.permission === "denied") {
    return false;
  }
  const permission = await Notification.requestPermission();
  return permission === "granted";
};

const fetchFcmToken = async (messaging) => {
  const sw = await ensureServiceWorker();
  if (!sw) {
    return null;
  }
  return getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: sw,
  }).catch(() => null);
};

const attachForegroundHandler = (messaging, handler) => {
  if (!handler) {
    return;
  }
  if (foregroundUnsubscribe) {
    foregroundUnsubscribe();
  }
  foregroundUnsubscribe = onMessage(messaging, (payload) => handler(payload));
};

export const initWebPush = async ({ userId, onForegroundMessage } = {}) => {
  if (!userId || typeof window === "undefined") {
    return;
  }
  if (initializing) {
    await initializing;
    return;
  }
  initializing = (async () => {
    try {
      const messaging = await ensureMessaging();
      if (!messaging) {
        return;
      }
      const granted = await requestBrowserPermission();
      if (!granted) {
        return;
      }
      const token = await fetchFcmToken(messaging);
      if (!token) {
        return;
      }
      attachForegroundHandler(messaging, onForegroundMessage);
      if (!shouldSyncToken(token, userId)) {
        return;
      }
      const deviceId = getOrCreateDeviceId();
      await ApiService.registerDeviceToken({
        fcmToken: token,
        deviceId,
      }).catch(() => {});
      storeToken(token, userId);
    } catch (e) {
    }
  })();
  await initializing;
  initializing = null;
};
